import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAutocompleteSuggestions,
  type Suggestion,
} from "../../../api/geocode";
import "./PlaceAutocompleteInput.less";

type Props = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onSelect?: (suggestion: Suggestion) => void;
  onBlur?: () => void;
  invalid?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

const DEBOUNCE_MS = 250;
const MIN_CHARS = 3;

export function PlaceAutocompleteInput({
  id,
  value,
  onChange,
  onSelect: onSelectProp,
  onBlur,
  invalid,
  placeholder,
  disabled,
}: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);
  const fetchIdRef = useRef(0);
  const lastSelectedLabelRef = useRef<string | null>(null);
  const lastFetchedTextRef = useRef<string>("");
  const listboxId = `${id}-listbox`;

  const handleInput = useCallback(
    (text: string) => {
      onChange(text);
      if (lastSelectedLabelRef.current && text !== lastSelectedLabelRef.current) {
        lastSelectedLabelRef.current = null;
      }
      clearTimeout(timerRef.current);
      if (text.length < MIN_CHARS) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      timerRef.current = setTimeout(async () => {
        const fetchId = ++fetchIdRef.current;
        try {
          const results = await fetchAutocompleteSuggestions(text);
          if (fetchIdRef.current !== fetchId) return;
          setSuggestions(results);
          lastFetchedTextRef.current = text;
          setOpen(results.length > 0);
          setActiveIndex(-1);
        } catch {
          if (fetchIdRef.current !== fetchId) return;
          setSuggestions([]);
          setOpen(false);
        }
      }, DEBOUNCE_MS);
    },
    [onChange],
  );

  const select = useCallback(
    (suggestion: Suggestion) => {
      onChange(suggestion.label);
      onSelectProp?.(suggestion);
      lastSelectedLabelRef.current = suggestion.label;
      setOpen(false);
      setSuggestions([]);
      setActiveIndex(-1);
    },
    [onChange, onSelectProp],
  );

  const handleBlur = useCallback(async () => {
    clearTimeout(timerRef.current);
    if (value.length < MIN_CHARS || lastSelectedLabelRef.current === value) {
      onBlur?.();
      return;
    }
    let results = (lastFetchedTextRef.current === value && suggestions.length > 0)
      ? suggestions
      : null;
    if (!results) {
      const fetchId = ++fetchIdRef.current;
      try {
        results = await fetchAutocompleteSuggestions(value);
        if (fetchIdRef.current !== fetchId) return;
      } catch {
        onBlur?.();
        return;
      }
    }
    if (results.length > 0) {
      select(results[0]);
    } else {
      onBlur?.();
    }
  }, [value, suggestions, select, onBlur]);

  const handleFocus = useCallback(async () => {
    if (value.length < MIN_CHARS) return;
    const fetchId = ++fetchIdRef.current;
    try {
      const results = await fetchAutocompleteSuggestions(value);
      if (fetchIdRef.current !== fetchId) return;
      setSuggestions(results);
      lastFetchedTextRef.current = value;
      setOpen(results.length > 0);
      setActiveIndex(-1);
    } catch {}
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter": {
          e.preventDefault();
          const idx = activeIndex >= 0 ? activeIndex : 0;
          if (idx < suggestions.length) {
            select(suggestions[idx]);
          }
          break;
        }
        case "Escape":
          e.preventDefault();
          setOpen(false);
          break;
      }
    },
    [open, activeIndex, suggestions, select],
  );

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="place-autocomplete" ref={containerRef}>
      <input
        id={id}
        role="combobox"
        type="text"
        className={invalid ? "is-invalid" : undefined}
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-invalid={invalid || undefined}
        aria-activedescendant={
          activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
        }
      />
      {open && suggestions.length > 0 && (
        <ul id={listboxId} role="listbox" className="place-autocomplete__list">
          {suggestions.map((s, i) => (
            <li
              key={s.id}
              id={`${id}-option-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={
                "place-autocomplete__option" +
                (i === activeIndex ? " place-autocomplete__option--active" : "")
              }
              onMouseDown={(e) => {
                e.preventDefault();
                select(s);
              }}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
